/**
 * Checklist localStorage storage utility
 *
 * Handles persistent storage of checklist state with 24-hour expiration.
 * Key format: mv_create_checklist_{creation_id}
 *
 * Schema:
 * {
 *   ingredients: { "group_0_item_0": true, "group_0_item_1": false },
 *   instructions: { "step_0": true, "step_1": false },
 *   collapsedGroups: ["group_1", "group_2"],
 *   timestamp: 1705420800000
 * }
 */

const KEY_PREFIX = 'mv_create_checklist_'
const EXPIRY_MS = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

/**
 * Check if localStorage is available
 * @return {boolean}
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__mv_storage_test__'
    localStorage.setItem(testKey, testKey)
    localStorage.removeItem(testKey)
    return true
  } catch (e) {
    return false
  }
}

/**
 * Generate storage key for a creation
 * @param {number|string} creationId - The Create card ID
 * @return {string}
 */
const getKey = (creationId) => `${KEY_PREFIX}${creationId}`

/**
 * Check if a timestamp has expired (older than 24 hours)
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @return {boolean}
 */
export const isExpired = (timestamp) => {
  if (!timestamp) {
    return true
  }
  return Date.now() - timestamp > EXPIRY_MS
}

/**
 * Get checklist state from localStorage
 * Returns null if not found or expired
 * @param {number|string} creationId - The Create card ID
 * @return {object|null}
 */
export const getChecklistState = (creationId) => {
  if (!isLocalStorageAvailable()) {
    return null
  }

  try {
    const key = getKey(creationId)
    const stored = localStorage.getItem(key)

    if (!stored) {
      return null
    }

    const state = JSON.parse(stored)

    // Check if state has expired
    if (isExpired(state.timestamp)) {
      // Clean up expired state
      localStorage.removeItem(key)
      return null
    }

    return state
  } catch (err) {
    // Handle JSON parse errors or other issues
    return null
  }
}

/**
 * Save checklist state to localStorage with current timestamp
 * @param {number|string} creationId - The Create card ID
 * @param {object} state - The checklist state to save
 * @return {boolean} - Whether the save was successful
 */
export const setChecklistState = (creationId, state) => {
  if (!isLocalStorageAvailable()) {
    return false
  }

  try {
    const key = getKey(creationId)
    const stateWithTimestamp = {
      ...state,
      timestamp: Date.now(),
    }

    localStorage.setItem(key, JSON.stringify(stateWithTimestamp))
    return true
  } catch (err) {
    // Handle quota exceeded or other storage errors
    return false
  }
}

/**
 * Clear all expired checklist states from localStorage
 * Useful for cleanup on app initialization
 * @return {number} - Number of expired entries removed
 */
export const clearExpiredChecklists = () => {
  if (!isLocalStorageAvailable()) {
    return 0
  }

  let removed = 0

  try {
    const keys = []

    // Collect all checklist keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(KEY_PREFIX)) {
        keys.push(key)
      }
    }

    // Check each key for expiration
    keys.forEach((key) => {
      try {
        const stored = localStorage.getItem(key)
        if (stored) {
          const state = JSON.parse(stored)
          if (isExpired(state.timestamp)) {
            localStorage.removeItem(key)
            removed++
          }
        }
      } catch (err) {
        // Remove corrupted entries
        localStorage.removeItem(key)
        removed++
      }
    })
  } catch (err) {
    // Handle any unexpected errors
  }

  return removed
}

/**
 * Create an empty checklist state object
 * @return {object}
 */
export const createEmptyState = () => ({
  ingredients: {},
  instructions: {},
  collapsedGroups: [],
  timestamp: Date.now(),
})
